import { DEFAULT_THEME_PALETTE, THEME_PALETTES } from "constants/themes";

export const THEME_PALETTE_STORAGE_KEY = "theme-palette";

// The free colour picker the palettes replaced saved a hex here, which painted
// the top menu alone. Nothing reads it any more.
const LEGACY_THEME_COLOR_KEY = "themeColor";

const isKnownPalette = (id) =>
  THEME_PALETTES.some((palette) => palette.id === id);

export function getStoredThemePalette() {
  try {
    const stored = localStorage.getItem(THEME_PALETTE_STORAGE_KEY);
    return isKnownPalette(stored) ? stored : DEFAULT_THEME_PALETTE;
  } catch {
    return DEFAULT_THEME_PALETTE;
  }
}

/** Repaints every token-driven colour at once: the palettes are `html[data-theme]` blocks. */
export function applyThemePalette(id) {
  const palette = isKnownPalette(id) ? id : DEFAULT_THEME_PALETTE;
  document.documentElement.setAttribute("data-theme", palette);
}

export function persistThemePalette(id) {
  try {
    localStorage.setItem(THEME_PALETTE_STORAGE_KEY, id);
  } catch {
    // Private-mode/quota failures just mean the choice lasts until the tab closes.
  }
}

// Runs before the first render, so the page never flashes the default palette. The
// sign-in pages carry the platform's own wordmark and artwork, so they keep the default.
// This covers the first paint; App.js re-applies off the auth state, since a 401 signs out
// without a reload.
export function initThemePalette({ signedIn }) {
  try {
    localStorage.removeItem(LEGACY_THEME_COLOR_KEY);
  } catch {
    // Nothing to clean up without storage.
  }
  applyThemePalette(signedIn ? getStoredThemePalette() : DEFAULT_THEME_PALETTE);
}

/**
 * A solid `--color-*` token of the active palette as `#rrggbb`, for code that
 * cannot read a CSS variable (AntD's theme algorithm, ApexCharts). Read it at
 * render time, never at module load, or a palette change will not reach it.
 */
export function readThemeColor(token) {
  const triplet = getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  const channels = triplet.split(/\s+/).map(Number);

  if (channels.length !== 3 || channels.some(Number.isNaN)) {
    return "";
  }

  return `#${channels.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}
